import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { useLang } from "../LanguageContext";
import { Chapter, Reveal } from "./Chapter";

const WA_URL = "https://api.whatsapp.com/send?phone=393317642541";

const TiltCover = ({ src = "/images/libro.jpg", alt = "Ma che ti dice il cervello?", testid = "book-cover" }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 140, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 140, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div style={{ perspective: 900 }} className="mx-auto max-w-xs lg:max-w-none">
      <motion.div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={() => { mx.set(0); my.set(0); }}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative shadow-2xl shadow-forest/30"
      >
        <img
          src={src}
          alt={alt}
          data-testid={testid}
          className="w-full object-cover"
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "linear-gradient(115deg, rgba(255,255,255,0.22) 0%, transparent 40%)" }}
        />
      </motion.div>
    </div>
  );
};

const Books = () => {
  const { t } = useLang();
  const b = t.books;

  return (
    <Chapter id="libri" num={b.num} kicker={b.kicker} title={b.title} className="bg-sand">
      <div className="grid items-center gap-16 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <TiltCover />
        </Reveal>
        <div className="lg:col-span-8">
          <Reveal delay={0.1}>
            <h3 className="mb-3 font-serif text-2xl font-light tracking-tight text-forest sm:text-3xl">{b.nameA}</h3>
            <p className="font-serif text-xl font-light italic text-terra sm:text-2xl">{b.sub}</p>
            {b.intro.map((p, i) => (
              <p key={i} className="mt-5 max-w-2xl text-base font-light leading-relaxed text-ink/85">{p}</p>
            ))}
            <ul className="mt-6 space-y-2.5">
              {b.questions.map((q, i) => (
                <li key={i} className="flex gap-3 text-sm font-light text-moss sm:text-base">
                  <span className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-terra" />
                  {q}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-3">
              {[...b.volumes, b.set].map((v, i) => (
                <div key={i} data-testid={`book-card-${i + 1}`} className={`p-6 ${i === 2 ? "bg-forest text-linen" : "bg-linen"}`}>
                  <p className={`font-mono text-[10px] uppercase tracking-[0.25em] ${i === 2 ? "text-gold" : "text-terra"}`}>{v.label}</p>
                  <p className="mt-3 font-serif text-3xl font-light">{v.price}</p>
                  {v.pages && <p className={`mt-1 text-xs font-light ${i === 2 ? "text-linen/60" : "text-moss"}`}>{v.pages}</p>}
                </div>
              ))}
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-moss">{b.format}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 border-l-2 border-terra pl-6">
              <h3 className="font-serif text-xl font-normal text-forest">{b.orderTitle}</h3>
              <p className="mt-3 max-w-xl text-sm font-light leading-relaxed text-moss">{b.orderText}</p>
              <a
                data-testid="book-order-whatsapp"
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-6 inline-flex items-center gap-3 rounded-full bg-forest px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-linen transition-colors duration-300 hover:bg-terra"
              >
                <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
                {b.cta}
              </a>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="mt-24 grid items-center gap-16 border-t border-ink/10 pt-20 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <Reveal>
            <h3 className="font-serif text-2xl font-light tracking-tight text-forest sm:text-3xl">{b.prodigio.name}</h3>
            <p className="mt-3 font-serif text-xl font-light italic text-terra sm:text-2xl">{b.prodigio.sub}</p>
            {b.prodigio.intro.map((p, i) => (
              <p key={i} className="mt-5 max-w-2xl text-base font-light leading-relaxed text-ink/85">{p}</p>
            ))}
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2">
              <div data-testid="book-card-prodigio" className="bg-linen p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra">{b.prodigio.publisher}</p>
                <p className="mt-3 font-serif text-3xl font-light">{b.prodigio.price}</p>
                <p className="mt-1 text-xs font-light text-moss">{b.prodigio.priceLabel}</p>
              </div>
              <div className="flex items-center bg-linen p-6">
                <a
                  data-testid="prodigio-order-whatsapp"
                  href={WA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 rounded-full bg-forest px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-linen transition-colors duration-300 hover:bg-terra"
                >
                  <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
                  {b.cta}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal className="order-first lg:order-none lg:col-span-4">
          <TiltCover src="/images/prodigio.jpg" alt="Il Prodigio della Vita" testid="book-cover-prodigio" />
        </Reveal>
      </div>
    </Chapter>
  );
};

export default Books;
